/**
 * TodayProgress — "Mission Control" widget above Needs Attention.
 *
 * Gives admins a felt-sense of momentum as they burn through the inbox.
 * Purely a client-side derivation on top of `total_actions` from
 * `/admin/actions-summary` (the same authoritative Needs Attention
 * lifecycle counter). Nothing is written to the backend.
 *
 * Model (each field is stored per-user per-day in localStorage so a
 * refresh mid-day doesn't lose the counters):
 *
 *   - **completed_today** — monotonic. Increments every time
 *     `total_actions` DROPS. Never decreases mid-day. Reset at midnight.
 *   - **new_today**       — monotonic. Increments every time
 *     `total_actions` RISES. Tracks new work that landed today; used
 *     so new tasks don't silently reduce the completed counter.
 *   - **open_start**      — `total_actions` at the moment we first
 *     opened the dashboard today (baseline for the "net" calculation).
 *   - **last_seen**       — last `total_actions` value we observed, so
 *     we can detect deltas across renders / refreshes.
 *
 * Derived readouts shown in the pill:
 *     completed = completed_today
 *     open_now  = total_actions (live)
 *     net       = open_now - open_start  (negative → making progress)
 *     pct       = completed / (completed + open_now)
 */
import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, ListTodo, TrendingDown, TrendingUp, Sparkles } from 'lucide-react';

const storageKey = (userId) => `todayProgress:v2:${userId || 'anon'}`;

const todayKey = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const readState = (userId) => {
    try {
        const raw = localStorage.getItem(storageKey(userId));
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const writeState = (userId, next) => {
    try { localStorage.setItem(storageKey(userId), JSON.stringify(next)); } catch { /* ignore */ }
};

/** Freshly-initialised bucket for today's baseline. */
const initialState = (openNow) => ({
    date: todayKey(),
    open_start: openNow,
    last_seen: openNow,
    completed_today: 0,
    new_today: 0,
});

/**
 * useAnimatedNumber — tweens a number toward the target over ~450ms so
 * completions feel intentional but never obtrusive. Skips animation on
 * initial mount so the first paint isn't animated from 0.
 */
const useAnimatedNumber = (value) => {
    const [display, setDisplay] = useState(value);
    const rafRef = useRef();
    const firstRender = useRef(true);
    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            setDisplay(value);
            return undefined;
        }
        const start = display;
        const delta = value - start;
        if (delta === 0) return undefined;
        const duration = 450;
        const t0 = performance.now();
        const step = (now) => {
            const t = Math.min(1, (now - t0) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setDisplay(Math.round(start + delta * eased));
            if (t < 1) rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(rafRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return display;
};

const TodayProgress = ({ userId, totalActions }) => {
    const openNow = Math.max(0, totalActions || 0);

    // Lazy init so we hydrate synchronously from localStorage before the
    // first paint — avoids a one-frame flash where completed_today looks
    // like it reset.
    const [state, setState] = useState(() => {
        const key = todayKey();
        const stored = readState(userId);
        if (stored && stored.date === key) {
            return { ...stored };
        }
        return initialState(openNow);
    });

    // Reconcile whenever `totalActions` moves. Two rules:
    //   1. If date rolled over → reset everything to current baseline.
    //   2. Otherwise: diff against `last_seen` and split the delta into
    //      completed vs new-work counters (both monotonic).
    //
    // New tasks landing during the day only bump `new_today` (and
    // `open_start` is untouched), so `completed_today` never decreases.
    useEffect(() => {
        setState((prev) => {
            const key = todayKey();
            if (prev.date !== key) {
                const next = initialState(openNow);
                writeState(userId, next);
                return next;
            }
            if (openNow === prev.last_seen) return prev;
            const delta = openNow - prev.last_seen;
            const next = {
                ...prev,
                last_seen: openNow,
                completed_today: prev.completed_today + (delta < 0 ? -delta : 0),
                new_today: prev.new_today + (delta > 0 ? delta : 0),
            };
            writeState(userId, next);
            return next;
        });
    }, [openNow, userId]);

    // Watchdog: if the browser is left open across midnight, roll the
    // baseline forward on the next tick without needing a refresh.
    useEffect(() => {
        const iv = setInterval(() => {
            setState((prev) => {
                const key = todayKey();
                if (prev.date === key) return prev;
                const next = initialState(openNow);
                writeState(userId, next);
                return next;
            });
        }, 60 * 1000);
        return () => clearInterval(iv);
    }, [openNow, userId]);

    const completed = state.completed_today;
    const net = openNow - state.open_start;
    const denom = completed + openNow;
    const pct = denom > 0 ? Math.min(100, Math.round((completed / denom) * 100)) : 0;

    const animatedCompleted = useAnimatedNumber(completed);
    const animatedOpen = useAnimatedNumber(openNow);

    // Nothing meaningful to show if there's never been any work today
    // AND nothing has been resolved.
    if (openNow === 0 && completed === 0) return null;

    const allClear = openNow === 0;
    const netLabel = net === 0
        ? '±0'
        : `${net > 0 ? '+' : '−'}${Math.abs(net)}`;
    const NetIcon = net < 0 ? TrendingDown : (net > 0 ? TrendingUp : null);
    const netTone = net < 0
        ? 'text-emerald-700'
        : (net > 0 ? 'text-amber-700' : 'text-slate-500');

    return (
        <div
            className="rounded-lg border border-slate-200 bg-white overflow-hidden"
            data-testid="today-progress-widget"
        >
            <div className="flex items-center justify-between px-4 py-2.5 gap-4">
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-1.5 rounded-md ${allClear ? 'bg-emerald-50' : 'bg-indigo-50'}`}>
                        {allClear
                            ? <Sparkles className="h-4 w-4 text-emerald-600" />
                            : <CheckCircle2 className="h-4 w-4 text-indigo-600" />}
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide leading-tight">
                            Today&apos;s Progress
                        </p>
                        {allClear && completed > 0 ? (
                            <p className="text-sm text-slate-900 leading-tight">
                                Inbox zero — <span className="font-semibold text-emerald-700">{animatedCompleted} completed today</span>.
                            </p>
                        ) : (
                            <p className="text-sm text-slate-900 leading-tight flex flex-wrap items-center gap-x-1.5">
                                <span>
                                    <span className="font-semibold text-emerald-700" data-testid="today-progress-completed">{animatedCompleted}</span>
                                    <span className="text-slate-500"> completed</span>
                                </span>
                                <span className="text-slate-300">·</span>
                                <span>
                                    <span className="font-semibold text-slate-900" data-testid="today-progress-open">{animatedOpen}</span>
                                    <span className="text-slate-500"> open now</span>
                                </span>
                                <span className="text-slate-300">·</span>
                                <span className={`inline-flex items-center gap-0.5 ${netTone}`} data-testid="today-progress-net">
                                    {NetIcon && <NetIcon className="h-3 w-3" />}
                                    <span className="font-medium">net {netLabel}</span>
                                    <span className="text-slate-500 ml-1">today</span>
                                </span>
                            </p>
                        )}
                    </div>
                </div>
                <div
                    className="hidden sm:flex items-center gap-1.5 text-[11px] text-slate-500 flex-shrink-0"
                    title="Completed / (Completed + Open now)"
                >
                    <ListTodo className="h-3.5 w-3.5" />
                    <span data-testid="today-progress-pct">{pct}%</span>
                </div>
            </div>
            <div
                className="h-1.5 bg-slate-100 relative overflow-hidden"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                data-testid="today-progress-bar"
            >
                <div
                    className={`h-full transition-[width] duration-500 ease-out ${allClear ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
};

export default TodayProgress;
